import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePets } from "@/hooks/usePets";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Heart, Edit, Trash2, Camera, Image as ImageIcon } from "lucide-react";
import { NovoPetModal } from "@/components/NovoPetModal";
import { EditarPetModal } from "@/components/EditarPetModal";
import DetalhesPetModal from "@/components/DetalhesPetModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Pets() {
  const { profile } = useAuth();
  const { pets, isLoading, deletePet } = usePets();
  const [novoPetModalOpen, setNovoPetModalOpen] = useState(false);
  const [editPetModal, setEditPetModal] = useState<{ open: boolean; pet?: any }>({
    open: false,
  });
  const [detalhesPetModal, setDetalhesPetModal] = useState<{ open: boolean; pet?: any }>({
    open: false,
  });

  const canCreatePet = profile?.role === "morador";
  const myPets = pets.filter(pet => pet.owner_id === profile?.id);
  const visiblePets = pets.filter(pet => pet.owner_id !== profile?.id && pet.visible_to_residents);

  const handleDeletePet = (petId: string) => {
    deletePet(petId);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getAgeText = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} meses`;
    }
    const years = Math.floor(ageInMonths / 12);
    return `${years} ano${years > 1 ? 's' : ''}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Carregando pets...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Pets</h1>
          <p className="text-muted-foreground">Gerencie os pets do condomínio</p>
        </div>
        {canCreatePet && (
          <Button onClick={() => setNovoPetModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Adicionar Pet
          </Button>
        )}
      </div>

      {/* Meus Pets */}
      {myPets.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Heart className="mr-2 h-5 w-5 text-red-500" />
            Meus Pets
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myPets.map((pet) => (
              <Card key={pet.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetalhesPetModal({ open: true, pet })}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{pet.name}</CardTitle>
                      <p className="text-sm text-muted-foreground capitalize">
                        {pet.type} • {pet.breed}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditPetModal({ open: true, pet });
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover Pet</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja remover {pet.name}? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeletePet(pet.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remover
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {pet.photo_url && (
                    <div className="mb-3">
                      <img
                        src={pet.photo_url}
                        alt={pet.name}
                        className="w-full h-32 object-cover rounded-md"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    {pet.color && (
                      <p className="text-sm">
                        <span className="font-medium">Cor:</span> {pet.color}
                      </p>
                    )}
                    {pet.birth_date && (
                      <p className="text-sm">
                        <span className="font-medium">Idade:</span> {getAgeText(pet.birth_date)}
                      </p>
                    )}
                    {pet.sex && (
                      <p className="text-sm">
                        <span className="font-medium">Sexo:</span> {pet.sex}
                      </p>
                    )}
                    {pet.size && (
                      <p className="text-sm">
                        <span className="font-medium">Porte:</span> {pet.size}
                      </p>
                    )}
                    {pet.weight && (
                      <p className="text-sm">
                        <span className="font-medium">Peso:</span> {pet.weight}kg
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <Badge variant={pet.visible_to_residents ? "default" : "secondary"}>
                        {pet.visible_to_residents ? "Visível" : "Privado"}
                      </Badge>
                      {pet.vaccination_card_url && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={pet.vaccination_card_url} target="_blank" rel="noopener noreferrer">
                            <ImageIcon className="h-4 w-4 mr-1" />
                            Carteira
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Pets do Condomínio */}
      {visiblePets.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Pets do Condomínio</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visiblePets.map((pet) => (
              <Card key={pet.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetalhesPetModal({ open: true, pet })}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{pet.name}</CardTitle>
                  <p className="text-sm text-muted-foreground capitalize">
                    {pet.type} • {pet.breed}
                  </p>
                  {pet.unit_number && (
                    <p className="text-xs text-muted-foreground">
                      Unidade: {pet.unit_number}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {pet.photo_url && (
                    <div className="mb-3">
                      <img
                        src={pet.photo_url}
                        alt={pet.name}
                        className="w-full h-32 object-cover rounded-md"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    {pet.color && (
                      <p className="text-sm">
                        <span className="font-medium">Cor:</span> {pet.color}
                      </p>
                    )}
                    {pet.birth_date && (
                      <p className="text-sm">
                        <span className="font-medium">Idade:</span> {getAgeText(pet.birth_date)}
                      </p>
                    )}
                    {pet.sex && (
                      <p className="text-sm">
                        <span className="font-medium">Sexo:</span> {pet.sex}
                      </p>
                    )}
                    {pet.size && (
                      <p className="text-sm">
                        <span className="font-medium">Porte:</span> {pet.size}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {pets.length === 0 && (
        <div className="text-center py-12">
          <Heart className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhum pet cadastrado</h3>
          <p className="text-muted-foreground mb-4">
            {canCreatePet 
              ? "Comece adicionando o primeiro pet do condomínio." 
              : "Nenhum pet foi cadastrado ainda."
            }
          </p>
          {canCreatePet && (
            <Button onClick={() => setNovoPetModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Primeiro Pet
            </Button>
          )}
        </div>
      )}

      <NovoPetModal
        open={novoPetModalOpen}
        onOpenChange={setNovoPetModalOpen}
      />

      {editPetModal.pet && (
        <EditarPetModal
          open={editPetModal.open}
          onOpenChange={(open) => setEditPetModal({ open, pet: open ? editPetModal.pet : undefined })}
          pet={editPetModal.pet}
        />
      )}

      {detalhesPetModal.pet && (
        <DetalhesPetModal
          open={detalhesPetModal.open}
          onOpenChange={(open) => setDetalhesPetModal({ open, pet: open ? detalhesPetModal.pet : undefined })}
          pet={detalhesPetModal.pet}
        />
      )}
      </div>
    </Layout>
  );
}